import { useState, useEffect, useRef } from 'react';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Database,
  Download,
  Upload,
  Loader2,
  ShieldAlert,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  FileArchive,
  Trash2,
  Shield,
  Clock,
} from 'lucide-react';
import { backupApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(isoString) {
  if (!isoString) return '-';
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoString;
  }
}

const STAGE_LABELS = {
  preparing: 'Preparando',
  exporting: 'Exportando collections',
  creating_archive: 'Criando arquivo',
  compressing: 'Comprimindo',
  validating: 'Validando',
  validating_file: 'Validando arquivo',
  validating_paths: 'Verificando caminhos',
  reading_manifest: 'Lendo manifest',
  validating_collections: 'Validando collections',
  creating_pre_restore_backup: 'Criando backup de segurança',
  restoring_collections: 'Restaurando collections',
  done: 'Concluído',
  queued: 'Na fila',
};

export default function BackupRestorePage() {
  const { user } = useAuth();
  const isAuthorized = user?.role === 'super_admin';

  // Backup state
  const [backups, setBackups] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [backupTaskId, setBackupTaskId] = useState(null);
  const [backupProgress, setBackupProgress] = useState(null);

  // Restore state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunTaskId, setDryRunTaskId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [restoreProgress, setRestoreProgress] = useState(null);

  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  // Load backups list
  const loadBackups = async () => {
    setLoadingList(true);
    try {
      const { data } = await backupApi.list();
      setBackups(data.backups || []);
    } catch (e) {
      toast.error('Erro ao listar backups');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (isAuthorized) loadBackups();
  }, [isAuthorized]);

  // Poll task status
  const pollTaskStatus = async (taskId, onComplete, onError) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await backupApi.getStatus(taskId);
        if (data.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          onComplete(data);
        } else if (data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          onError(data);
        } else {
          setBackupProgress(data);
          setRestoreProgress(data);
        }
      } catch (e) {
        // Keep polling on transient errors
      }
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // --- BACKUP ---
  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    setBackupProgress(null);
    try {
      const { data } = await backupApi.create();
      setBackupTaskId(data.task_id);
      pollTaskStatus(
        data.task_id,
        (task) => {
          setCreatingBackup(false);
          setBackupProgress(null);
          toast.success('Backup criado com sucesso!');
          loadBackups();
        },
        (task) => {
          setCreatingBackup(false);
          setBackupProgress(null);
          toast.error(`Falha no backup: ${task.error || 'Erro desconhecido'}`);
        }
      );
    } catch (e) {
      setCreatingBackup(false);
      const msg = e?.response?.data?.detail || 'Erro ao criar backup';
      toast.error(msg);
    }
  };

  // --- RESTORE ---
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setDryRunResult(null);
      setRestoreResult(null);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setDryRunResult(null);
    try {
      const { data } = await backupApi.upload(selectedFile);
      setDryRunTaskId(data.task_id);
      setDryRunResult(data.result?.dry_run || null);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erro na análise';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmRestore = async () => {
    if (!dryRunTaskId) return;
    setRestoring(true);
    setRestoreResult(null);
    setConfirmDialogOpen(false);
    try {
      const { data } = await backupApi.confirmRestore(dryRunTaskId);
      setRestoreResult(data);
      if (data.status === 'completed') {
        toast.success('Restauração concluída com sucesso!');
        loadBackups();
      } else {
        toast.error(`Restauração falhou: ${data.error || 'Erro desconhecido'}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erro na restauração';
      toast.error(msg);
    } finally {
      setRestoring(false);
    }
  };

  if (!isAuthorized) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-32 text-center">
          <ShieldAlert className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Acesso negado</h2>
          <p className="text-[#A1A1AA]">Apenas Super Admin pode acessar esta página.</p>
        </div>
      </MainLayout>
    );
  }

  const latestBackup = backups.find((b) => b.type === 'manual') || backups[0];

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Database className="w-6 h-6" />
            Backup e Restauração
          </h1>
          <p className="text-[#A1A1AA] mt-1">
            Gerencie backups do banco de dados do e-Torneios
          </p>
        </div>

        {/* Backup Card */}
        <Card className="bg-[#141414] border-[#27272A]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Database className="w-5 h-5" />
              Backup do E-Torneios
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-[#A1A1AA] text-sm">
              Crie uma cópia completa do banco de dados.
            </p>

            {creatingBackup && backupProgress && (
              <div className="bg-[#1A1A2E] border border-[#27272A] rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-[#EAB308]">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Criando backup...</span>
                </div>
                <p className="text-xs text-[#A1A1AA]">
                  {STAGE_LABELS[backupProgress.stage] || backupProgress.stage}
                  {backupProgress.progress ? ` (${backupProgress.progress})` : ''}
                </p>
              </div>
            )}

            <Button
              onClick={handleCreateBackup}
              disabled={creatingBackup}
              className="bg-[#EAB308] hover:bg-[#CA8A04] text-black font-semibold"
            >
              {creatingBackup ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Criando...
                </>
              ) : (
                <>
                  <Database className="w-4 h-4 mr-2" />
                  Criar Backup
                </>
              )}
            </Button>

            {latestBackup && (
              <div className="border-t border-[#27272A] pt-4">
                <p className="text-xs text-[#A1A1AA] mb-2">Último backup:</p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="text-white">{latestBackup.filename}</span>
                  <span className="text-[#A1A1AA]">{formatDate(latestBackup.created_at)}</span>
                  <span className="text-[#A1A1AA]">{formatBytes(latestBackup.file_size)}</span>
                  {latestBackup.collections > 0 && (
                    <span className="text-[#A1A1AA]">{latestBackup.collections} collections</span>
                  )}
                  {latestBackup.documents > 0 && (
                    <span className="text-[#A1A1AA]">{latestBackup.documents.toLocaleString()} docs</span>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 border-[#27272A] text-white hover:bg-[#1F1F2E]"
                  onClick={() => window.open(backupApi.download(latestBackup.id), '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Backups List */}
        <Card className="bg-[#141414] border-[#27272A]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 justify-between">
              <span className="flex items-center gap-2">
                <FileArchive className="w-5 h-5" />
                Backups Existentes
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadBackups}
                disabled={loadingList}
                className="text-[#A1A1AA] hover:text-white"
              >
                <RefreshCw className={`w-4 h-4 ${loadingList ? 'animate-spin' : ''}`} />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingList ? (
              <div className="flex items-center gap-2 text-[#A1A1AA] py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                Carregando...
              </div>
            ) : backups.length === 0 ? (
              <p className="text-[#A1A1AA] py-4">Nenhum backup encontrado.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="tabela w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#27272A]">
                      <th className="text-left py-2 text-[#A1A1AA] font-medium">Arquivo</th>
                      <th className="text-left py-2 text-[#A1A1AA] font-medium">Data</th>
                      <th className="text-left py-2 text-[#A1A1AA] font-medium">Tipo</th>
                      <th className="text-right py-2 text-[#A1A1AA] font-medium">Tamanho</th>
                      <th className="text-right py-2 text-[#A1A1AA] font-medium">Collections</th>
                      <th className="text-right py-2 text-[#A1A1AA] font-medium">Documentos</th>
                      <th className="text-right py-2 text-[#A1A1AA] font-medium">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backups.map((backup) => (
                      <tr key={backup.id} className="border-b border-[#1F1F2E] hover:bg-[#1A1A2E]">
                        <td className="py-3 text-white font-mono text-xs">
                          {backup.filename}
                        </td>
                        <td className="py-3 text-[#A1A1AA]">
                          {formatDate(backup.created_at)}
                        </td>
                        <td className="py-3">
                          {backup.type === 'pre_restore' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-900/30 text-yellow-400 border border-yellow-800">
                              <Shield className="w-3 h-3" />
                              Pré-restore
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-900/30 text-green-400 border border-green-800">
                              <Database className="w-3 h-3" />
                              Manual
                            </span>
                          )}
                        </td>
                        <td className="py-3 text-right text-[#A1A1AA]">
                          {formatBytes(backup.file_size)}
                        </td>
                        <td className="py-3 text-right text-[#A1A1AA]">
                          {backup.collections || '-'}
                        </td>
                        <td className="py-3 text-right text-[#A1A1AA]">
                          {(backup.documents || 0).toLocaleString()}
                        </td>
                        <td className="py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(backupApi.download(backup.id), '_blank')}
                              className="h-8 px-2 text-[#A1A1AA] hover:text-white"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Restore Card */}
        <Card className="bg-[#141414] border-[#27272A]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Restaurar Backup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-[#A1A1AA] text-sm">
              Selecione um arquivo de backup (.tar.gz) para analisar e restaurar.
            </p>

            <div className="flex items-center gap-4">
              <Input
                ref={fileInputRef}
                type="file"
                accept=".tar.gz"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="border-[#27272A] text-white hover:bg-[#1F1F2E]"
              >
                <Upload className="w-4 h-4 mr-2" />
                Selecionar Arquivo
              </Button>
              {selectedFile && (
                <span className="text-sm text-[#A1A1AA]">
                  {selectedFile.name} ({formatBytes(selectedFile.size)})
                </span>
              )}
            </div>

            {selectedFile && !dryRunResult && (
              <Button
                onClick={handleAnalyze}
                disabled={uploading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analisando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Analisar Backup
                  </>
                )}
              </Button>
            )}

            {/* Dry-run result */}
            {dryRunResult && (
              <div className="space-y-4">
                <div className="bg-[#1A1A2E] border border-[#27272A] rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Resultado da Análise
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      Arquivo íntegro
                    </div>
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      Manifest encontrado
                    </div>
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      Estrutura válida
                    </div>
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      {dryRunResult.collections_found} collections encontradas
                    </div>
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      {dryRunResult.total_docs_parsed.toLocaleString()} documentos encontrados
                    </div>
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      Extended JSON válido
                    </div>
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      Banco de destino: {dryRunResult.db_name}
                    </div>
                    {dryRunResult.warnings && dryRunResult.warnings.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {dryRunResult.warnings.map((w, i) => (
                          <div key={i} className="flex items-center gap-2 text-yellow-400 text-xs">
                            <AlertTriangle className="w-3 h-3" />
                            {w}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-green-900/20 border border-green-800 rounded-lg p-3">
                  <p className="text-green-400 text-sm font-semibold flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    BACKUP PRONTO PARA RESTAURAÇÃO
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDryRunResult(null);
                      setSelectedFile(null);
                      setDryRunTaskId(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="border-[#27272A] text-white hover:bg-[#1F1F2E]"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={() => setConfirmDialogOpen(true)}
                    disabled={!dryRunResult.all_valid}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Confirmar Restauração
                  </Button>
                </div>
              </div>
            )}

            {/* Restore progress */}
            {restoring && restoreProgress && (
              <div className="bg-[#1A1A2E] border border-[#27272A] rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-[#EAB308]">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Restaurando banco...</span>
                </div>
                <p className="text-xs text-[#A1A1AA]">
                  {STAGE_LABELS[restoreProgress.stage] || restoreProgress.stage}
                </p>
              </div>
            )}

            {/* Restore result */}
            {restoreResult && (
              <div className={`border rounded-lg p-4 ${
                restoreResult.status === 'completed'
                  ? 'bg-green-900/20 border-green-800'
                  : 'bg-red-900/20 border-red-800'
              }`}>
                {restoreResult.status === 'completed' ? (
                  <div className="space-y-2">
                    <p className="text-green-400 font-semibold flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      RESTAURAÇÃO CONCLUÍDA
                    </p>
                    <div className="text-sm text-[#A1A1AA] space-y-1">
                      <p>Collections restauradas: {restoreResult.result?.collections_restored || 0}</p>
                      <p>Documentos inseridos: {(restoreResult.result?.total_docs_inserted || 0).toLocaleString()}</p>
                      {restoreResult.result?.pre_restore_backup && (
                        <p className="text-yellow-400">
                          Backup de segurança: {restoreResult.result.pre_restore_backup}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-red-400 font-semibold flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      RESTAURAÇÃO NÃO CONCLUÍDA
                    </p>
                    <p className="text-sm text-[#A1A1AA]">
                      {restoreResult.error || 'Erro desconhecido'}
                    </p>
                    {restoreResult.result?.pre_restore_backup && (
                      <p className="text-sm text-yellow-400">
                        Backup de segurança preservado: {restoreResult.result.pre_restore_backup}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Confirm Dialog */}
        <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <DialogContent className="bg-[#141414] border-[#27272A]">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Restauração do Banco
              </DialogTitle>
              <DialogDescription className="text-[#A1A1AA]">
                Esta operação substituirá os dados atuais.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-white">
                Você está prestes a restaurar o banco de dados do e-Torneios.
              </p>
              <div className="bg-[#1A1A2E] rounded-lg p-3 space-y-1">
                <p className="text-[#A1A1AA]">
                  Arquivo: <span className="text-white">{selectedFile?.name}</span>
                </p>
                <p className="text-[#A1A1AA]">
                  Collections: <span className="text-white">{dryRunResult?.collections_found}</span>
                </p>
                <p className="text-[#A1A1AA]">
                  Documentos: <span className="text-white">{dryRunResult?.total_docs_parsed?.toLocaleString()}</span>
                </p>
              </div>
              <p className="text-yellow-400 text-xs flex items-center gap-1">
                <Shield className="w-3 h-3" />
                Um backup de segurança será criado automaticamente antes da restauração.
              </p>
              <p className="text-yellow-400 text-xs">
                Se o backup de segurança falhar, a restauração será cancelada.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmDialogOpen(false)}
                className="border-[#27272A] text-white hover:bg-[#1F1F2E]"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmRestore}
                disabled={restoring}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {restoring ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Restaurando...
                  </>
                ) : (
                  'Confirmar Restauração'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
